import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface BSNode {
  id: string;
  code: string;
  name: string;
  type: string;
  parent_id: string | null;
  balance: string;
  children: BSNode[];
}

interface BSResponse {
  as_of: string;
  asset: BSNode[];
  liability: BSNode[];
  equity: BSNode[];
  totals: {
    asset: string;
    liability: string;
    equity: string;
    current_period_earnings: string;
  };
}

interface RowProps {
  node: BSNode;
  depth: number;
  flipSign: boolean;
}

// Roll a node's balance up from its descendants — parents carry 0 in the
// raw query because journal_lines never reference them directly.
function rollupBalance(node: BSNode): bigint {
  let sum = BigInt(node.balance);
  for (const child of node.children) sum += rollupBalance(child);
  return sum;
}

function ReportRow({ node, depth, flipSign }: RowProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const raw = hasChildren ? rollupBalance(node) : BigInt(node.balance);
  const display = flipSign ? -raw : raw;
  const isZero = display === 0n;

  return (
    <>
      <tr className="border-b text-sm hover:bg-slate-50">
        <td
          className="py-1 font-mono text-xs text-slate-500"
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {hasChildren && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mr-1 text-slate-400 hover:text-slate-600"
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
          {node.code}
        </td>
        <td className="px-2 py-1" style={{ paddingLeft: `${depth * 16 + 8}px` }}>
          {node.name}
        </td>
        <td className={`px-2 py-1 text-right font-mono text-sm ${isZero ? 'text-slate-300' : ''}`}>
          {display.toString()}
        </td>
      </tr>
      {expanded &&
        hasChildren &&
        node.children.map((child) => (
          <ReportRow key={child.id} node={child} depth={depth + 1} flipSign={flipSign} />
        ))}
    </>
  );
}

export function BalanceSheet() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));

  const { data, isLoading, error } = useQuery({
    queryKey: ['balance-sheet', activeOrgId, asOf],
    queryFn: () => api<BSResponse>(`/bookkeeping/reports/balance-sheet?as_of=${asOf}`),
    enabled: !!activeOrgId && !!asOf,
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('balanceSheet.title')}</h1>

      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-600">
          {t('balanceSheet.asOf')}:
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="ml-2 rounded border px-2 py-1 text-sm"
          />
        </label>
      </div>

      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="text-sm text-red-600">{(error as { message?: string }).message ?? 'Error'}</p>
      )}

      {data && (
        <div className="space-y-4">
          <Section
            title={t('balanceSheet.assets')}
            nodes={data.asset}
            flipSign={false}
            total={data.totals.asset}
            totalLabel={t('balanceSheet.total_assets')}
          />
          <Section
            title={t('balanceSheet.liabilities')}
            nodes={data.liability}
            flipSign={true}
            total={(-BigInt(data.totals.liability)).toString()}
            totalLabel={t('balanceSheet.total_liabilities')}
          />
          <Section
            title={t('balanceSheet.equity')}
            nodes={data.equity}
            flipSign={true}
            total={(
              -BigInt(data.totals.equity) + BigInt(data.totals.current_period_earnings)
            ).toString()}
            totalLabel={t('balanceSheet.total_equity')}
            extraRow={{
              label: t('balanceSheet.current_period_earnings'),
              value: data.totals.current_period_earnings,
            }}
          />
          <Invariant t={t} totals={data.totals} />
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  nodes,
  flipSign,
  total,
  totalLabel,
  extraRow,
}: {
  title: string;
  nodes: BSNode[];
  flipSign: boolean;
  total: string;
  totalLabel: string;
  extraRow?: { label: string; value: string };
}) {
  return (
    <div className="overflow-x-auto rounded border bg-white">
      <h2 className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">{title}</h2>
      <table className="w-full">
        <tbody>
          {nodes.map((n) => (
            <ReportRow key={n.id} node={n} depth={0} flipSign={flipSign} />
          ))}
          {extraRow && (
            <tr className="border-b text-sm">
              <td className="py-1 pl-2 font-mono text-xs text-slate-500" colSpan={2}>
                <em className="not-italic text-slate-700">{extraRow.label}</em>
              </td>
              <td className="px-2 py-1 text-right font-mono text-sm">{extraRow.value}</td>
            </tr>
          )}
          <tr className="border-t bg-slate-50 text-sm font-semibold">
            <td className="px-2 py-2" colSpan={2}>
              {totalLabel}
            </td>
            <td className="px-2 py-2 text-right font-mono">{total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Invariant({ t, totals }: { t: (k: string) => string; totals: BSResponse['totals'] }) {
  // Asset = -(Liability + Equity) + current_period_earnings
  const a = BigInt(totals.asset);
  const lhs = a;
  const rhs =
    -BigInt(totals.liability) - BigInt(totals.equity) + BigInt(totals.current_period_earnings);
  const diff = lhs - rhs;
  const balanced = diff === 0n;
  return (
    <div
      className={`rounded border px-4 py-3 text-sm ${
        balanced
          ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
          : 'border-rose-300 bg-rose-50 text-rose-900'
      }`}
    >
      {balanced
        ? t('balanceSheet.balanced')
        : `${t('balanceSheet.unbalanced')}: diff=${diff.toString()}`}
    </div>
  );
}
